import type {
  Cliente,
  PedidoComItens,
  GatewayPagamento,
  DetalheOperacionalPedido,
  PedidoEvento,
  StatusOperacionalPedido,
  Usuario,
  CategoriaServico,
  EnvioPedido,
} from '@pgcustom/shared-types';
import {
  STATUS_OPERACIONAL_PEDIDO,
  ROTULO_STATUS_OPERACIONAL,
  MENSAGEM_PUBLICA_PADRAO_STATUS_OPERACIONAL,
  rotuloOrigemPedido,
  rotuloReferenciaPedido,
} from '@pgcustom/shared-types';
import { formatBRL, centavosParaReais, formatarCpfExibicao } from '@pgcustom/shared-utils';
import { ApiError } from '../../core/api-client.js';
import type { PedidosApi } from './pedidos.api.js';
import type { ClientesApi } from '../clientes/clientes.api.js';
import type { OpsApi } from '../producao/ops.api.js';
import type { ComissoesApi } from '../comissoes/comissoes.api.js';
import type { UsuariosApi } from '../usuarios/usuarios.api.js';
import type { PermissoesPedidos } from './pedidos.permissoes.js';
import { rotuloStatusPedido } from './status-pedido.js';
import { formatarDataHora } from './formatacao.js';

const GATEWAYS: Array<{ valor: GatewayPagamento; rotulo: string }> = [
  { valor: 'mercado_pago', rotulo: 'Mercado Pago' },
  { valor: 'infinitepay', rotulo: 'InfinitePay' },
  { valor: 'stone', rotulo: 'Stone' },
];

const ROTULO_GATEWAY: Record<GatewayPagamento, string> = {
  mercado_pago: 'Mercado Pago',
  infinitepay: 'InfinitePay',
  stone: 'Stone',
  manual: 'Manual',
};

/** Mesmos rótulos de `envios.page.ts` — replicados aqui (3 entradas, sem custo de manter em sincronia). */
const ROTULO_STATUS_ENVIO: Record<string, string> = {
  criado: 'Aguardando despacho',
  despachado: 'Em trânsito',
  entregue: 'Entregue',
};

/**
 * `<erp-pedido-detalhe-painel>` — painel operacional completo do Pedido, servido
 * como VISÃO da própria página de Pedidos (não mais um `<pg-modal>`): a listagem
 * dá lugar a este painel quando um pedido é aberto, com "← Voltar para pedidos"
 * no cabeçalho (mesmo padrão da referência visual `pg-custom-pedido-admin.html`).
 *
 * Arquitetura visual fiel à referência: cabeçalho do pedido + grade de duas
 * colunas (coluna principal com cards de Resumo financeiro / Informações gerais /
 * Cliente / Endereço / Produtos / chips / Frete / Pagamento / Documentos /
 * Previsão + Equipe / Nota interna, e coluna lateral fixa com Alterar status /
 * Timeline / Imprimir·Exportar / Ações).
 *
 * TODO dado vem do backend real (`GET /pedidos/:id`, `/eventos`,
 * `/detalhe-operacional`, `ClientesApi`, `UsuariosApi`, `ComissoesApi`). Toda
 * ação persiste via API real:
 * - status → `POST /pedidos/:id/eventos` (evento novo, nunca sobrescreve)
 * - pagamento manual → `POST /pedidos/:id/pagamento-manual` (gateway `manual`
 *   + evento com a origem como nota interna; guarda de status impede duplicação)
 * - previsão → `PATCH /ops/:id` (`prazo`) + mensagem pública opcional como evento
 * - equipe → `PATCH /ops/:id` (`tecnicoId`/`categoriaServicoId`)
 * Nota interna NUNCA é serializada para o Portal do Cliente (garantia de tipo em
 * `paraEventoPublico`, `@pgcustom/shared-types`).
 */
export class ErpPedidoDetalhePainel extends HTMLElement {
  private readonly root: ShadowRoot;
  private _pedidosApi: PedidosApi | null = null;
  private _clientesApi: ClientesApi | null = null;
  private _opsApi: OpsApi | null = null;
  private _comissoesApi: ComissoesApi | null = null;
  private _usuariosApi: UsuariosApi | null = null;
  private _permissoes: PermissoesPedidos = { ler: false, criar: false, atualizar: false };
  private _aoAtualizar: (() => void) | null = null;
  private _aoVoltar: (() => void) | null = null;

  private pedidoId: string | null = null;
  private carregando = false;
  private erroCarregar = '';
  private pedido: PedidoComItens | null = null;
  private cliente: Cliente | null = null;
  /** Composição só-leitura (OPS/Envio/Comissão) — `null` até carregar, ou quando nada existe ainda pro pedido. */
  private detalheOperacional: DetalheOperacionalPedido | null = null;
  /** Visão admin completa (inclui nota interna) — nunca repassada ao Portal do Cliente a partir daqui. */
  private eventos: PedidoEvento[] = [];

  private enviandoAcao = false;
  private mensagemAcao = '';
  private mostrarFormCancelar = false;

  private novoEventoStatus: StatusOperacionalPedido = 'solicitado';
  private novoEventoMensagem = '';
  private novoEventoNota = '';
  private enviandoEvento = false;
  private mensagemEvento = '';

  private enviandoPagamentoManual = false;
  private mensagemPagamentoManual = '';

  /** Rascunho local do form de previsão — só existe quando `detalheOperacional.ops` existe. */
  private previsaoDataHora = '';
  private previsaoMensagemCliente = '';
  private enviandoPrevisao = false;
  private mensagemPrevisao = '';

  /** Colaboradores/categorias reais — carregados só quando a OPS já existe (equipe é atribuída na OPS, `PATCH /ops/:id`). */
  private usuarios: Usuario[] = [];
  private categorias: CategoriaServico[] = [];
  private enviandoEquipe = false;
  private mensagemEquipe = '';

  private enviandoNotaInterna = false;
  private mensagemNotaInterna = '';

  /** Documento em processamento (`''` = nenhum) e último erro ao obter documento. */
  private documentoCarregando: '' | 'official' | 'production' = '';
  private mensagemDocumento = '';

  /** ENVIO E RASTREAMENTO — vem da ponte com o site (`shipping_labels`). `null` = ainda não carregado ou indisponível. */
  private envio: EnvioPedido | null = null;
  private envioIndisponivel = false;
  private rastreioTransportadora = '';
  private rastreioCodigo = '';
  private salvandoRastreio = false;
  private mensagemRastreio = '';

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set pedidosApi(valor: PedidosApi) {
    this._pedidosApi = valor;
  }
  set clientesApi(valor: ClientesApi) {
    this._clientesApi = valor;
  }
  set opsApi(valor: OpsApi) {
    this._opsApi = valor;
  }
  set comissoesApi(valor: ComissoesApi) {
    this._comissoesApi = valor;
  }
  set usuariosApi(valor: UsuariosApi) {
    this._usuariosApi = valor;
  }
  set permissoes(valor: PermissoesPedidos) {
    this._permissoes = valor;
  }
  set aoAtualizar(valor: () => void) {
    this._aoAtualizar = valor;
  }
  set aoVoltar(valor: () => void) {
    this._aoVoltar = valor;
  }

  connectedCallback(): void {
    this.render();
  }

  abrirParaVer(pedidoId: string): void {
    this.pedidoId = pedidoId;
    this.carregando = true;
    this.erroCarregar = '';
    this.pedido = null;
    this.cliente = null;
    this.detalheOperacional = null;
    this.eventos = [];
    this.mensagemAcao = '';
    this.mostrarFormCancelar = false;
    this.novoEventoStatus = 'solicitado';
    this.novoEventoMensagem = '';
    this.novoEventoNota = '';
    this.mensagemEvento = '';
    this.mensagemPagamentoManual = '';
    this.previsaoDataHora = '';
    this.previsaoMensagemCliente = '';
    this.mensagemPrevisao = '';
    this.usuarios = [];
    this.categorias = [];
    this.mensagemEquipe = '';
    this.mensagemNotaInterna = '';
    this.documentoCarregando = '';
    this.mensagemDocumento = '';
    this.envio = null;
    this.envioIndisponivel = false;
    this.rastreioTransportadora = '';
    this.rastreioCodigo = '';
    this.salvandoRastreio = false;
    this.mensagemRastreio = '';
    this.render();
    void this.carregar(pedidoId);
  }

  private async carregar(pedidoId: string): Promise<void> {
    if (!this._pedidosApi) return;

    try {
      const pedido = await this._pedidosApi.obterPorId(pedidoId);
      this.pedido = pedido;

      if (this._clientesApi) {
        try {
          this.cliente = await this._clientesApi.buscarPorId(pedido.clienteId);
        } catch {
          this.cliente = null;
        }
      }

      await Promise.all([
        this.carregarDetalheOperacional(pedidoId),
        this.carregarEventos(pedidoId),
        this.carregarRastreio(pedidoId),
      ]);
      this.novoEventoStatus = this.eventoMaisRecenteStatus();
    } catch (erro) {
      this.erroCarregar = erro instanceof ApiError ? erro.message : 'não foi possível carregar o pedido.';
    } finally {
      this.carregando = false;
      this.render();
    }
  }

  private async carregarDetalheOperacional(pedidoId: string): Promise<void> {
    if (!this._pedidosApi) return;
    try {
      this.detalheOperacional = await this._pedidosApi.obterDetalheOperacional(pedidoId);
    } catch {
      this.detalheOperacional = null;
    }

    const ops = this.detalheOperacional?.ops;
    if (!ops) return;

    // Rascunhos dos forms de Previsão/Equipe começam com o valor real já salvo na OPS.
    this.previsaoDataHora = ops.prazo ? isoParaDatetimeLocal(ops.prazo) : '';

    try {
      const [resultadoUsuarios, resultadoCategorias] = await Promise.all([
        this._usuariosApi?.listar() ?? Promise.resolve({ status: 'vazio' as const }),
        this._comissoesApi?.listarCategorias() ?? Promise.resolve({ status: 'vazio' as const }),
      ]);
      this.usuarios = resultadoUsuarios.status === 'ok' ? resultadoUsuarios.dado : [];
      this.categorias = resultadoCategorias.status === 'ok' ? resultadoCategorias.dado : [];
    } catch {
      this.usuarios = [];
      this.categorias = [];
    }
  }

  private async carregarEventos(pedidoId: string): Promise<void> {
    if (!this._pedidosApi) return;
    try {
      this.eventos = await this._pedidosApi.listarEventos(pedidoId);
    } catch {
      this.eventos = [];
    }
  }

  private async carregarRastreio(pedidoId: string): Promise<void> {
    if (!this._pedidosApi) return;
    try {
      this.envio = await this._pedidosApi.obterRastreio(pedidoId);
      this.envioIndisponivel = false;
      // Pré-preenche o formulário com o rastreio já salvo (para correção).
      if (this.envio.rastreio) {
        this.rastreioCodigo = this.envio.rastreio.trackingCode;
        this.rastreioTransportadora =
          this.envio.rastreio.carrierName && this.envio.rastreio.carrierName !== 'Correios'
            ? this.envio.rastreio.carrierName
            : '';
      }
    } catch (erro) {
      // 503 = SITE_SERVICE_TOKEN ausente no ERP (integração não configurada) — estado honesto,
      // nunca um card quebrado. Qualquer outra falha também cai aqui.
      this.envio = null;
      this.envioIndisponivel = erro instanceof ApiError && erro.status === 503;
    }
  }

  private async salvarRastreio(): Promise<void> {
    if (!this._pedidosApi || !this.pedidoId || this.salvandoRastreio) return;
    const codigo = this.rastreioCodigo.trim();
    if (!codigo) {
      this.mensagemRastreio = 'Informe o código de rastreio.';
      this.render();
      return;
    }
    this.salvandoRastreio = true;
    this.mensagemRastreio = '';
    this.render();
    try {
      const transportadora = this.rastreioTransportadora.trim();
      await this._pedidosApi.salvarRastreio(this.pedidoId, {
        codigoRastreio: codigo,
        ...(transportadora ? { transportadora } : {}),
      });
      await this.carregarRastreio(this.pedidoId);
      this.mensagemRastreio = 'Rastreio salvo.';
    } catch (erro) {
      this.mensagemRastreio = erro instanceof ApiError ? erro.message : 'não foi possível salvar o rastreio.';
    } finally {
      this.salvandoRastreio = false;
      this.render();
    }
  }

  private async copiarCodigoRastreio(): Promise<void> {
    const codigo = this.envio?.rastreio?.trackingCode;
    if (!codigo) return;
    try {
      await navigator.clipboard.writeText(codigo);
      this.mensagemRastreio = 'Código copiado.';
    } catch {
      this.mensagemRastreio = `Código: ${codigo}`;
    }
    this.render();
  }

  private voltar(): void {
    if (this.enviandoAcao || this.enviandoEvento || this.enviandoPagamentoManual) return;
    this._aoVoltar?.();
  }

  private async pagar(gateway: GatewayPagamento): Promise<void> {
    if (!this._pedidosApi || !this.pedido || this.enviandoAcao) return;
    this.enviandoAcao = true;
    this.mensagemAcao = '';
    this.render();
    try {
      const atualizado = await this._pedidosApi.pagar(this.pedido.id, gateway);
      this.pedido = { ...this.pedido, ...atualizado };
      this._aoAtualizar?.();
    } catch (erro) {
      this.mensagemAcao = erro instanceof ApiError ? erro.message : 'não foi possível marcar como pago.';
    } finally {
      this.enviandoAcao = false;
      this.render();
    }
  }

  private async cancelar(motivo: string): Promise<void> {
    if (!this._pedidosApi || !this.pedido || this.enviandoAcao) return;
    if (!motivo.trim()) {
      this.mensagemAcao = 'motivo é obrigatório';
      this.render();
      return;
    }
    this.enviandoAcao = true;
    this.mensagemAcao = '';
    this.render();
    try {
      const atualizado = await this._pedidosApi.cancelar(this.pedido.id, motivo.trim());
      this.pedido = { ...this.pedido, ...atualizado };
      this.mostrarFormCancelar = false;
      this._aoAtualizar?.();
    } catch (erro) {
      this.mensagemAcao = erro instanceof ApiError ? erro.message : 'não foi possível cancelar o pedido.';
    } finally {
      this.enviandoAcao = false;
      this.render();
    }
  }

  /**
   * Registra uma atualização na timeline — mensagem ao cliente e nota interna são campos SEMPRE
   * separados (`criarEventoPedidoSchema`). Nunca sobrescreve: cada chamada cria uma linha nova.
   */
  private async registrarEvento(): Promise<void> {
    if (!this._pedidosApi || !this.pedido || this.enviandoEvento) return;
    this.enviandoEvento = true;
    this.mensagemEvento = '';
    this.render();
    try {
      await this._pedidosApi.adicionarEvento(this.pedido.id, {
        statusOperacional: this.novoEventoStatus,
        ...(this.novoEventoMensagem.trim() ? { mensagemCliente: this.novoEventoMensagem.trim() } : {}),
        ...(this.novoEventoNota.trim() ? { notaInterna: this.novoEventoNota.trim() } : {}),
      });
      this.novoEventoMensagem = '';
      this.novoEventoNota = '';
      await this.carregarEventos(this.pedido.id);
      this.novoEventoStatus = this.eventoMaisRecenteStatus();
    } catch (erro) {
      this.mensagemEvento = erro instanceof ApiError ? erro.message : 'não foi possível registrar a atualização.';
    } finally {
      this.enviandoEvento = false;
      this.render();
    }
  }

  /** Adiciona SÓ uma nota interna — persistida como evento (todo registro do pedido é evento) com o status operacional atual. Nunca chega ao cliente. */
  private async adicionarNotaInterna(texto: string): Promise<void> {
    if (!this._pedidosApi || !this.pedido || this.enviandoNotaInterna) return;
    if (!texto.trim()) {
      this.mensagemNotaInterna = 'escreva a nota antes de salvar';
      this.render();
      return;
    }
    this.enviandoNotaInterna = true;
    this.mensagemNotaInterna = '';
    this.render();
    try {
      await this._pedidosApi.adicionarEvento(this.pedido.id, {
        statusOperacional: this.eventoMaisRecenteStatus(),
        notaInterna: texto.trim(),
      });
      await this.carregarEventos(this.pedido.id);
    } catch (erro) {
      this.mensagemNotaInterna = erro instanceof ApiError ? erro.message : 'não foi possível salvar a nota interna.';
    } finally {
      this.enviandoNotaInterna = false;
      this.render();
    }
  }

  private async arquivar(): Promise<void> {
    if (!this._pedidosApi || !this.pedido || this.enviandoAcao) return;
    this.enviandoAcao = true;
    this.mensagemAcao = '';
    this.render();
    try {
      const atualizado = await this._pedidosApi.arquivar(this.pedido.id);
      this.pedido = { ...this.pedido, ...atualizado };
      this._aoAtualizar?.();
    } catch (erro) {
      this.mensagemAcao = erro instanceof ApiError ? erro.message : 'não foi possível arquivar o pedido.';
    } finally {
      this.enviandoAcao = false;
      this.render();
    }
  }

  private async restaurar(): Promise<void> {
    if (!this._pedidosApi || !this.pedido || this.enviandoAcao) return;
    this.enviandoAcao = true;
    this.mensagemAcao = '';
    this.render();
    try {
      const atualizado = await this._pedidosApi.restaurar(this.pedido.id);
      this.pedido = { ...this.pedido, ...atualizado };
      this._aoAtualizar?.();
    } catch (erro) {
      this.mensagemAcao = erro instanceof ApiError ? erro.message : 'não foi possível restaurar o pedido.';
    } finally {
      this.enviandoAcao = false;
      this.render();
    }
  }

  /**
   * Pagamento recebido fora do fluxo automático (PIX direto, dinheiro, transferência etc.) —
   * `POST /pedidos/:id/pagamento-manual`. A guarda de status do backend impede duplicação em
   * clique repetido (segundo POST vira 400 "não pode ser marcado como pago no status atual: pago").
   */
  private async confirmarPagamentoManual(origem: string, mensagemCliente: string): Promise<void> {
    if (!this._pedidosApi || !this.pedido || this.enviandoPagamentoManual) return;
    if (!origem.trim()) {
      this.mensagemPagamentoManual = 'origem é obrigatória (ex: PIX direto, dinheiro, transferência)';
      this.render();
      return;
    }
    this.enviandoPagamentoManual = true;
    this.mensagemPagamentoManual = '';
    this.render();
    try {
      const atualizado = await this._pedidosApi.confirmarPagamentoManual(this.pedido.id, {
        origem: origem.trim(),
        ...(mensagemCliente.trim() ? { mensagemCliente: mensagemCliente.trim() } : {}),
      });
      this.pedido = { ...this.pedido, ...atualizado };
      await this.carregarEventos(this.pedido.id);
      this._aoAtualizar?.();
    } catch (erro) {
      this.mensagemPagamentoManual = erro instanceof ApiError ? erro.message : 'não foi possível confirmar o pagamento.';
    } finally {
      this.enviandoPagamentoManual = false;
      this.render();
    }
  }

  /**
   * Previsão de conclusão — é `Ops.prazo` (`PATCH /ops/:id`, mesmo endpoint da página Produção).
   * A mensagem pública sobre a nova previsão é um `pedido_evento` (mesma timeline), não um campo à parte.
   */
  private async salvarPrevisao(dataHora: string, mensagemCliente: string): Promise<void> {
    const ops = this.detalheOperacional?.ops;
    if (!this._opsApi || !this.pedido || !ops || this.enviandoPrevisao) return;
    if (!dataHora) {
      this.mensagemPrevisao = 'selecione uma data';
      this.render();
      return;
    }
    this.enviandoPrevisao = true;
    this.mensagemPrevisao = '';
    this.render();
    try {
      await this._opsApi.atualizar(ops.id, { prazo: new Date(dataHora).toISOString() });
      if (mensagemCliente.trim()) {
        await this._pedidosApi?.adicionarEvento(this.pedido.id, {
          statusOperacional: this.eventoMaisRecenteStatus(),
          mensagemCliente: mensagemCliente.trim(),
        });
        this.previsaoMensagemCliente = '';
        await this.carregarEventos(this.pedido.id);
      }
      await this.carregarDetalheOperacional(this.pedido.id);
      this._aoAtualizar?.();
    } catch (erro) {
      this.mensagemPrevisao = erro instanceof ApiError ? erro.message : 'não foi possível salvar a previsão.';
    } finally {
      this.enviandoPrevisao = false;
      this.render();
    }
  }

  /**
   * Equipe responsável — `Ops.tecnicoId`/`categoriaServicoId` via `PATCH /ops/:id` (mesmo
   * endpoint/regra da página Produção). O ERP suporta 1 responsável por OPS hoje.
   */
  private async salvarEquipe(tecnicoId: string, categoriaServicoId: string): Promise<void> {
    const ops = this.detalheOperacional?.ops;
    if (!this._opsApi || !this.pedido || !ops || this.enviandoEquipe) return;
    this.enviandoEquipe = true;
    this.mensagemEquipe = '';
    this.render();
    try {
      await this._opsApi.atualizar(ops.id, {
        tecnicoId: tecnicoId || null,
        categoriaServicoId: categoriaServicoId || null,
      });
      await this.carregarDetalheOperacional(this.pedido.id);
      this._aoAtualizar?.();
    } catch (erro) {
      this.mensagemEquipe = erro instanceof ApiError ? erro.message : 'não foi possível salvar a equipe responsável.';
    } finally {
      this.enviandoEquipe = false;
      this.render();
    }
  }

  /** Status do evento mais recente (ou o inicial) — usado pra não inventar um status novo só porque a previsão/nota mudou. */
  private eventoMaisRecenteStatus(): StatusOperacionalPedido {
    return this.eventos.at(-1)?.statusOperacional ?? 'solicitado';
  }

  /** Exportação real dos dados já carregados — nenhuma chamada nova, nenhum dado inventado. */
  private exportarCsv(): void {
    const pedido = this.pedido;
    if (!pedido) return;
    const linhas = [
      ['Campo', 'Valor'],
      ['Identificação', rotuloReferenciaPedido(pedido)],
      ['ID técnico', pedido.id],
      ['Status', rotuloStatusPedido(pedido.status)],
      ['Cliente', this.cliente?.nome ?? pedido.clienteId],
      ['Subtotal', pedido.subtotalCentavos !== null ? formatBRL(centavosParaReais(pedido.subtotalCentavos)) : '—'],
      ['Desconto', pedido.descontoCentavos !== null ? formatBRL(centavosParaReais(pedido.descontoCentavos)) : '—'],
      ['Frete', pedido.freteCentavos !== null ? formatBRL(centavosParaReais(pedido.freteCentavos)) : '—'],
      ['Valor total', formatBRL(centavosParaReais(pedido.valorTotalCentavos))],
      ['Criado em', formatarDataHora(pedido.criadoEm)],
      ...(pedido.pagoEm ? [['Pago em', formatarDataHora(pedido.pagoEm)]] : []),
      ...pedido.itens.map((item, i) => [
        `Item ${i + 1}`,
        `${item.descricao} — ${item.quantidade}x ${formatBRL(centavosParaReais(item.valorUnitarioCentavos))}`,
      ]),
    ];
    const csv = linhas.map((linha) => linha.map((campo) => `"${campo.replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `pedido-${(pedido.numero ?? pedido.id.slice(0, 8)).replace(/\s+/g, '')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  // ------------------------------------------------------------------ render

  private render(): void {
    this.root.innerHTML = `<style>${CSS}</style>${this.renderConteudo()}`;
    this.wire();
  }

  private renderConteudo(): string {
    if (this.carregando) {
      return `
        <header class="top">
          <div><button class="btn voltar" data-acao="voltar">← Voltar para pedidos</button></div>
        </header>
        <pg-empty-state variant="carregando" titulo="Pedido"></pg-empty-state>`;
    }
    if (this.erroCarregar || !this.pedido) {
      return `
        <header class="top">
          <div><button class="btn voltar" data-acao="voltar">← Voltar para pedidos</button></div>
        </header>
        <pg-empty-state variant="${this.erroCarregar ? 'erro' : 'vazio'}" titulo="Pedido" descricao="${esc(this.erroCarregar)}"></pg-empty-state>`;
    }

    const p = this.pedido;
    // Só mostra o badge de status OPERACIONAL quando já existe pelo menos um evento — senão
    // um pedido pago/cancelado apareceria como "AGUARDANDO PAGAMENTO" (o default do enum), o
    // que confunde. Sem evento, o cabeçalho mostra só o status comercial.
    const temEventoOperacional = this.eventos.length > 0;

    return `
      <header class="top">
        <div>
          <div class="crumb">Pedidos › ${esc(rotuloReferenciaPedido(p))}</div>
          <h1>${esc(rotuloReferenciaPedido(p))}
            ${temEventoOperacional ? `<span class="status-badge">${esc(ROTULO_STATUS_OPERACIONAL[this.eventoMaisRecenteStatus()].toUpperCase())}</span>` : ''}
            <span class="status-badge ${p.status}">${esc(rotuloStatusPedido(p.status).toUpperCase())}</span>
          </h1>
          <div class="sub">Realizado em ${esc(formatarDataHora(p.criadoEm))} · <span class="ref-tecnica">${esc(idCurto(p.id))}</span>${p.arquivadoEm ? ' · <span class="tag-arquivado">Arquivado</span>' : ''}</div>
        </div>
        <div class="actions">
          <button class="btn voltar" data-acao="voltar">← Voltar para pedidos</button>
          <button class="btn" data-acao="imprimir">Imprimir resumo</button>
          <button class="btn" data-acao="exportar">Exportar CSV</button>
        </div>
      </header>

      <div class="grid">
        <section class="cards">
          ${this.cardResumoFinanceiro()}
          ${this.cardInformacoesGerais()}
          ${this.cardCliente()}
          ${this.cardEndereco()}
          ${this.cardProdutos()}
          ${this.cardChips()}
          ${this.cardFrete()}
          ${this.cardEnvioRastreamento()}
          ${this.cardPagamento()}
          ${this.cardDocumentos()}
          ${this.cardPrevisaoEEquipe()}
          ${this.cardNotaInterna()}
        </section>

        <aside class="right">
          <div class="sticky">
            ${this.cardAlterarStatus()}
            ${this.cardTimeline()}
            ${this.cardImprimirExportar()}
            ${this.cardAcoes()}
          </div>
        </aside>
      </div>
    `;
  }

  private cardResumoFinanceiro(): string {
    const p = this.pedido!;
    const semResumo = p.subtotalCentavos === null;
    return `
      <div class="card">
        <h2>Resumo financeiro</h2>
        <div class="row"><span>Subtotal</span><b>${p.subtotalCentavos !== null ? brl(p.subtotalCentavos) : DASH}</b></div>
        <div class="row"><span>Desconto</span><b class="${p.descontoCentavos ? 'green' : ''}">${p.descontoCentavos !== null ? `- ${brl(p.descontoCentavos)}` : DASH}</b></div>
        <div class="row"><span>Frete</span><b>${p.freteCentavos !== null ? brl(p.freteCentavos) : DASH}</b></div>
        <div class="row total"><span>Total</span><b>${brl(p.valorTotalCentavos)}</b></div>
        ${semResumo ? '<p class="nota">Pedido anterior ao resumo financeiro — só pedidos novos registram subtotal/desconto/frete.</p>' : ''}
      </div>`;
  }

  private cardInformacoesGerais(): string {
    const p = this.pedido!;
    const cupom = this.detalheOperacional?.cupom ?? null;
    // "Cupom" e "Parceiro" são dimensões distintas: cupom é o código que o cliente digitou;
    // parceiro é a quem a venda foi atribuída (pode existir sem cupom, e vice-versa).
    const cupomTexto = cupom
      ? `${esc(cupom.codigo)} <span class="nao-informado">(-${cupom.descontoPercentual}%)</span>`
      : p.cupomId
        ? 'Aplicado (código indisponível)'
        : 'Nenhum';
    return `
      <div class="card">
        <h2>Informações gerais</h2>
        <div class="row"><span>Identificação</span><span class="value">${esc(rotuloReferenciaPedido(p))}${p.numero ? '' : ' <span class="nao-informado">(número atribuído no pagamento)</span>'}</span></div>
        <div class="row"><span>ID técnico</span><span class="value nao-informado">${esc(p.id)}</span></div>
        <div class="row"><span>Status operacional</span><span class="value">${this.eventos.length > 0 ? esc(ROTULO_STATUS_OPERACIONAL[this.eventoMaisRecenteStatus()]) : '<span class="nao-informado">sem etapa registrada</span>'}</span></div>
        <div class="row"><span>Status comercial</span><span class="value">${esc(rotuloStatusPedido(p.status))}</span></div>
        <div class="row"><span>Cupom</span><span class="value">${cupomTexto}</span></div>
        <div class="row"><span>Parceiro atribuído</span><span class="value">${p.parceiroId ? 'Sim' : 'Nenhum'}</span></div>
        <div class="row"><span>Origem</span><span class="value">${esc(rotuloOrigemPedido(p.origem))}</span></div>
        <div class="row"><span>Gateway / Forma</span><span class="value">${p.gatewayPagamento ? esc(ROTULO_GATEWAY[p.gatewayPagamento] ?? p.gatewayPagamento) : DASH}</span></div>
        ${p.pagoEm ? `<div class="row"><span>Pago em</span><span class="value">${esc(formatarDataHora(p.pagoEm))}</span></div>` : ''}
      </div>`;
  }

  private cardCliente(): string {
    const p = this.pedido!;
    const c = this.cliente;
    return `
      <div class="card">
        <h2>Cliente</h2>
        <div class="row"><span>Nome</span><b>${esc(c?.nome ?? `(id ${idCurto(p.clienteId)})`)}</b></div>
        <div class="row"><span>E-mail</span><b>${esc(c?.email ?? DASH_TXT)}</b></div>
        <div class="row"><span>Telefone</span><b>${esc(c?.telefone ?? DASH_TXT)}</b></div>
        <div class="row"><span>CPF</span><b>${esc(c?.cpf ? formatarCpfExibicao(c.cpf) : DASH_TXT)}</b></div>
        <a class="smallbtn link" href="#/clientes">Ver perfil do cliente</a>
      </div>`;
  }

  private cardEndereco(): string {
    const e = this.pedido!.enderecoEntrega;
    if (!e) {
      return `<div class="card"><h2>Endereço de entrega</h2><p class="nota">Endereço não informado para este pedido.</p></div>`;
    }
    return `
      <div class="card">
        <h2>Endereço de entrega</h2>
        <p class="endereco">${esc(e.logradouro)}, ${esc(e.numero)}${e.complemento ? ` — ${esc(e.complemento)}` : ''}</p>
        <p class="endereco">${esc(e.bairro)} — ${esc(e.cidade)}/${esc(e.estado)}</p>
        <p class="endereco">CEP ${esc(e.cep)}</p>
      </div>`;
  }

  private cardProdutos(): string {
    const itens = this.pedido!.itens;
    // Miniatura REAL: só a imagem que o próprio cliente anexou para ESTE pedido (uploads do
    // site, via a ponte). Não existe outra fonte real hoje (o render do configurador não é
    // persistido). Sem imagem real → slot vazio honesto (monograma PG), nunca a imagem de
    // outro produto.
    const ref = this.envio?.referenceImage ?? null;
    const thumb = (primeiro: boolean): string => {
      if (primeiro && ref?.url) {
        return `<div class="produto-img"><img src="${esc(ref.url)}" alt="Referência enviada pelo cliente" loading="lazy" /></div>`;
      }
      return `<div class="produto-img produto-img--vazia"><span>PG</span></div>`;
    };
    return `
      <div class="card full">
        <h2>Produtos</h2>
        ${
          itens.length === 0
            ? '<p class="nota">Nenhum item registrado neste pedido.</p>'
            : itens
                .map(
                  (item, i) => `
          <div class="produto">
            ${thumb(i === 0)}
            <div><b>${esc(item.descricao)}</b><div class="muted">${i === 0 && ref?.url ? 'Referência enviada pelo cliente' : 'Produto personalizado'}</div></div>
            <b>${item.quantidade}</b>
            <b>${brl(item.quantidade * item.valorUnitarioCentavos)}</b>
          </div>`
                )
                .join('')
        }
        ${ref?.url ? '' : '<p class="nota">Sem imagem específica deste pedido (o site não persiste o render do configurador).</p>'}
      </div>`;
  }

  private cardChips(): string {
    const p = this.pedido!;
    const ops = this.detalheOperacional?.ops ?? null;
    const envio = this.detalheOperacional?.envio ?? null;
    const comissao = this.detalheOperacional?.comissaoParceiro ?? null;
    const cupom = this.detalheOperacional?.cupom ?? null;
    return `
      <div class="chips full">
        <div class="chip"><span class="muted">Cupom</span><b>${cupom ? esc(cupom.codigo) : p.cupomId ? 'Aplicado' : '—'}</b><small>${p.descontoCentavos ? `- ${brlTexto(p.descontoCentavos)}` : 'sem desconto'}</small></div>
        <div class="chip"><span class="muted">Frete</span><b>${envio?.transportadora ? esc(envio.transportadora) : '—'}</b><small>${p.freteCentavos !== null ? brlTexto(p.freteCentavos) : 'não informado'}</small></div>
        <div class="chip"><span class="muted">Previsão</span><b>${ops?.prazo ? esc(formatarDataHora(ops.prazo)) : '—'}</b><small>7 a 40 dias úteis</small></div>
        <div class="chip"><span class="muted">Parceiro / Comissão</span><b>${p.parceiroId ? 'Sim' : '—'}</b><small class="gold-text">${comissao ? brlTexto(comissao.valorCentavos) : 'sem comissão'}</small></div>
      </div>`;
  }

  /**
   * `true` quando o pedido veio da sincronização com o site (é pago mas ainda não tem OPS/
   * envio no ERP): produção, prazo, equipe e rastreio desse pedido são acompanhados no painel
   * antigo durante a transição — o job de sincronização replica só o comercial/financeiro
   * (migration 038), nunca `producao.*`/`envios.*`.
   */
  private producaoNoPainelAntigo(): boolean {
    return (
      this.pedido?.status === 'pago' &&
      !this.detalheOperacional?.ops &&
      !this.detalheOperacional?.envio
    );
  }

  private cardFrete(): string {
    const envio = this.detalheOperacional?.envio ?? null;
    if (!envio) {
      const p = this.pedido!;
      // Rastreio/transportadora agora ficam na seção "Envio e rastreamento" (abaixo, dado
      // real do site). Aqui só o custo do frete do pedido.
      return `<div class="card"><h2>Frete</h2>
        <div class="row"><span>Custo</span><b>${p.freteCentavos !== null ? brl(p.freteCentavos) : DASH_TXT}</b></div>
        <p class="nota">Transportadora e código de rastreio ficam na seção “Envio e rastreamento”.</p></div>`;
    }
    return `
      <div class="card">
        <h2>Frete <a class="link-secao" href="#/envios">Ver em Envios →</a></h2>
        <div class="row"><span>Status</span><b>${esc(ROTULO_STATUS_ENVIO[envio.status] ?? envio.status)}</b></div>
        <div class="row"><span>Transportadora</span><b>${envio.transportadora ? esc(envio.transportadora) : DASH_TXT}</b></div>
        <div class="row"><span>Código de rastreio</span><b>${envio.codigoRastreio ? esc(envio.codigoRastreio) : DASH_TXT}</b></div>
        <div class="row"><span>Custo</span><b>${this.pedido!.freteCentavos !== null ? brl(this.pedido!.freteCentavos) : DASH_TXT}</b></div>
        ${!envio.codigoRastreio ? '<p class="nota">O código de rastreio é registrado no módulo Envios ao despachar.</p>' : ''}
      </div>`;
  }

  /**
   * ENVIO E RASTREAMENTO — dado REAL do site (`shipping_labels`, lido pela ponte). O admin
   * cadastra/corrige transportadora + código aqui; o upsert e a idempotência do e-mail
   * "pedido enviado" acontecem no site (nunca reimplementados no ERP). Sem `SITE_SERVICE_TOKEN`
   * no ERP → estado honesto ("integração não configurada"), nunca um card quebrado.
   */
  private cardEnvioRastreamento(): string {
    const podeEditar = this._permissoes.atualizar;
    const r = this.envio?.rastreio ?? null;
    const msg = this.mensagemRastreio ? `<p class="nota-acao">${esc(this.mensagemRastreio)}</p>` : '';

    if (this.envioIndisponivel) {
      return `<div class="card full"><h2>Envio e rastreamento</h2>
        <p class="nota">Integração de rastreio com o site não configurada neste ambiente (SITE_SERVICE_TOKEN).</p></div>`;
    }

    const salvo = r
      ? `
        <div class="rastreio-salvo">
          <div class="row"><span>Transportadora</span><b>${esc(r.carrierName ?? 'Correios')}</b></div>
          <div class="row"><span>Código de rastreio</span><b class="mono">${esc(r.trackingCode)}</b></div>
          ${r.updatedAt ? `<div class="row"><span>Atualizado em</span><b>${esc(formatarDataHora(r.updatedAt))}</b></div>` : r.shippedAt ? `<div class="row"><span>Cadastrado em</span><b>${esc(formatarDataHora(r.shippedAt))}</b></div>` : ''}
          <div class="row"><span>Status do envio</span><b>${esc(this.rotuloStatusEnvioSite())}</b></div>
          <div class="row"><span>E-mail ao cliente</span><b>${r.emailSent ? 'Enviado' : 'Ainda não enviado'}</b></div>
          <div class="rastreio-acoes">
            ${r.trackingUrl ? `<a class="smallbtn gold" href="${esc(r.trackingUrl)}" target="_blank" rel="noopener noreferrer">Rastrear pedido</a>` : '<span class="nota">Sem link oficial para esta transportadora.</span>'}
            <button class="smallbtn" data-acao="copiar-rastreio">Copiar código</button>
          </div>
        </div>`
      : `<p class="nota">${this.notaSemRastreio()}</p>`;

    const form = podeEditar
      ? `
        <div class="rastreio-form">
          <label>Transportadora <span class="op">(opcional — Correios se vazio)</span>
            <input id="rastreio-transportadora" type="text" value="${esc(this.rastreioTransportadora)}" placeholder="Correios" ${this.salvandoRastreio ? 'disabled' : ''} />
          </label>
          <label>Código de rastreio
            <input id="rastreio-codigo" type="text" value="${esc(this.rastreioCodigo)}" placeholder="AA123456789BR" ${this.salvandoRastreio ? 'disabled' : ''} />
          </label>
          <button class="smallbtn gold" data-acao="salvar-rastreio" ${this.salvandoRastreio ? 'disabled' : ''}>${this.salvandoRastreio ? 'Salvando…' : r ? 'Atualizar rastreio' : 'Salvar rastreio'}</button>
        </div>`
      : '';

    return `<div class="card full">
      <h2>Envio e rastreamento</h2>
      ${salvo}
      ${form}
      ${msg}
    </div>`;
  }

  private rotuloStatusEnvioSite(): string {
    const s = this.envio?.shippingStatus ?? null;
    const mapa: Record<string, string> = {
      pending: 'Aguardando despacho',
      label_created: 'Etiqueta criada',
      shipped: 'Enviado',
      delivered: 'Entregue',
      returned: 'Devolvido',
    };
    return s ? mapa[s] ?? s : '—';
  }

  private notaSemRastreio(): string {
    const os = this.envio?.orderStatus ?? this.pedido?.status ?? null;
    const emProducao = ['paid', 'received', 'customization', 'assembly', 'quality_test', 'final_inspection'];
    if (os && emProducao.includes(os)) return 'Pedido em produção — o rastreio é cadastrado ao despachar.';
    if (os === 'canceled' || os === 'canceled_expired') return 'Pedido cancelado — sem envio.';
    return 'Rastreamento ainda não disponível.';
  }

  private cardPagamento(): string {
    const p = this.pedido!;
    const pode = this._permissoes.atualizar;
    const pendente = p.status === 'pendente';
    return `
      <div class="card">
        <h2>Pagamento</h2>
        <div class="row"><span>Forma / Gateway</span><b>${p.gatewayPagamento ? esc(ROTULO_GATEWAY[p.gatewayPagamento] ?? p.gatewayPagamento) : DASH_TXT}</b></div>
        <div class="row"><span>Status</span><b class="${p.status === 'pago' ? 'green' : 'gold-text'}">${esc(rotuloStatusPedido(p.status))}</b></div>
        <div class="row"><span>Valor</span><b>${brl(p.valorTotalCentavos)}</b></div>
        ${
          pode && pendente
            ? `
          <div class="form-bloco">
            <div class="field"><label>Origem do pagamento</label>
              <input id="pm-origem" type="text" placeholder="Ex: PIX direto, dinheiro, transferência" maxlength="255" ${this.enviandoPagamentoManual ? 'disabled' : ''} />
            </div>
            <div class="field"><label>Mensagem para o cliente (opcional)</label>
              <textarea id="pm-mensagem" maxlength="2000" ${this.enviandoPagamentoManual ? 'disabled' : ''}></textarea>
            </div>
            <button class="smallbtn gold" data-acao="pagamento-manual" ${this.enviandoPagamentoManual ? 'disabled' : ''}>${this.enviandoPagamentoManual ? 'Confirmando…' : 'Confirmar pagamento manualmente'}</button>
            ${this.mensagemPagamentoManual ? `<p class="erro">${esc(this.mensagemPagamentoManual)}</p>` : ''}
          </div>`
            : ''
        }
        ${!pendente && p.pagoEm ? `<p class="nota">Pagamento confirmado em ${esc(formatarDataHora(p.pagoEm))}.</p>` : ''}
      </div>`;
  }

  private cardDocumentos(): string {
    // PDF real renderizado ao vivo pelo motor de documentos do site legado
    // (`pgcustom-art/server/services/documentEngine`), servido pelo ERP via
    // `GET /pedidos/:id/documento`. O ERP não gera nada — só repassa.
    const p = this.pedido!;
    const pago = p.status === 'pago';
    const carregando = (t: string) => this.documentoCarregando === t;

    const acoes = (tipo: 'official' | 'production') => `
      <div class="document-actions">
        <button class="smallbtn gold" data-acao="doc:${tipo}:ver" ${carregando(tipo) ? 'disabled' : ''}>${carregando(tipo) ? 'Abrindo…' : 'Visualizar'}</button>
        <button class="smallbtn" data-acao="doc:${tipo}:baixar" ${carregando(tipo) ? 'disabled' : ''}>Baixar PDF</button>
        <button class="smallbtn" data-acao="doc:${tipo}:imprimir" ${carregando(tipo) ? 'disabled' : ''}>Imprimir</button>
      </div>`;

    return `
      <div class="card full">
        <h2>Documentos</h2>

        <div class="doc-bloco">
          <div class="doc-linha">
            <div>
              <b>${pago ? 'Ordem de Produção e Serviço (OPS)' : 'Orçamento do pedido'}</b>
              <div class="muted">${pago ? 'Documento operacional — renderizado ao vivo pelo motor de documentos do site' : 'Vira OPS automaticamente quando o pagamento é confirmado'}</div>
            </div>
            <span class="status-badge ${pago ? 'pago' : ''}">${pago ? 'OPS' : 'ORÇAMENTO'}</span>
          </div>
          ${acoes('official')}
        </div>

        ${
          pago
            ? `<div class="doc-bloco">
                <div class="doc-linha">
                  <div>
                    <b>Ordem de Produção — checklist de bancada</b>
                    <div class="muted">Uso interno da produção (sem valores)</div>
                  </div>
                </div>
                ${acoes('production')}
              </div>`
            : ''
        }

        <p class="nota">Nota Fiscal (NF-e): a emissão fiscal ainda não está integrada — nenhuma NF-e é gerada ou exibida aqui até essa integração existir.</p>
        ${this.mensagemDocumento ? `<p class="erro">${esc(this.mensagemDocumento)}</p>` : ''}
      </div>`;
  }

  /**
   * Busca o PDF real do documento (via `PedidosApi.baixarDocumento` → `GET /pedidos/:id/documento`)
   * e o abre / baixa / imprime. Nunca `window.print()` da página do ERP — sempre o PDF de verdade.
   */
  private async abrirDocumento(tipo: 'official' | 'production', modo: 'ver' | 'baixar' | 'imprimir'): Promise<void> {
    if (!this._pedidosApi || !this.pedido || this.documentoCarregando) return;
    this.documentoCarregando = tipo;
    this.mensagemDocumento = '';
    this.render();
    try {
      const blob = await this._pedidosApi.baixarDocumento(this.pedido.id, tipo);
      const url = URL.createObjectURL(blob);
      if (modo === 'baixar') {
        const a = document.createElement('a');
        a.href = url;
        a.download = `${tipo === 'production' ? 'ordem-producao' : 'documento'}-${this.pedido.id.slice(0, 8)}.pdf`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 15_000);
      } else {
        const janela = window.open(url, '_blank');
        if (!janela) {
          this.mensagemDocumento = 'permita pop-ups para abrir o documento em uma nova aba.';
        } else if (modo === 'imprimir') {
          // O PDF real abre no visualizador do navegador; dispara a impressão dele (não da página do ERP).
          setTimeout(() => {
            try {
              janela.focus();
              janela.print();
            } catch {
              /* alguns visualizadores bloqueiam print programático — o usuário imprime pelo próprio visualizador */
            }
          }, 900);
        }
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      }
    } catch (erro) {
      this.mensagemDocumento =
        erro instanceof ApiError ? erro.message : 'não foi possível obter o documento.';
    } finally {
      this.documentoCarregando = '';
      this.render();
    }
  }

  private cardPrevisaoEEquipe(): string {
    const ops = this.detalheOperacional?.ops ?? null;
    const pode = this._permissoes.atualizar;

    if (!ops) {
      const notaProducao = this.producaoNoPainelAntigo()
        ? 'Este pedido veio do site. Enquanto a produção não é migrada para o ERP, a etapa, o prazo e a equipe são acompanhados no painel antigo — a sincronização replica só o comercial e o financeiro.'
        : this.pedido!.status === 'pago'
          ? 'A Ordem de Produção (OPS) do ERP ainda não foi criada para este pedido.'
          : 'Previsão e equipe ficam disponíveis quando o pedido é pago e entra em produção.';
      return `
        <div class="card full">
          <div class="dupla">
            <div>
              <h2>Previsão de conclusão</h2>
              <div class="row"><span>Prazo oficial</span><b>7 a 40 dias úteis</b></div>
              <p class="nota">${notaProducao}</p>
            </div>
            <div>
              <h2>Equipe responsável</h2>
              <p class="nota">${notaProducao}</p>
            </div>
          </div>
        </div>`;
    }

    const tecnico = this.usuarios.find((u) => u.id === ops.tecnicoId);
    const categoria = this.categorias.find((c) => c.id === ops.categoriaServicoId);
    const disabledPrev = this.enviandoPrevisao || !pode;
    const disabledEq = this.enviandoEquipe || !pode;

    return `
      <div class="card full">
        <div class="dupla">
          <div>
            <h2>Previsão de conclusão</h2>
            <div class="row"><span>Prazo oficial</span><b>7 a 40 dias úteis</b></div>
            <div class="row"><span>Previsão atual</span><b>${ops.prazo ? esc(formatarDataHora(ops.prazo)) : '—'}</b></div>
            ${
              pode
                ? `
              <div class="field"><label>Nova previsão</label>
                <input id="prev-data" type="datetime-local" value="${esc(this.previsaoDataHora)}" ${disabledPrev ? 'disabled' : ''} />
              </div>
              <div class="field"><label>Mensagem para o cliente (opcional — visível no Portal do Cliente)</label>
                <textarea id="prev-mensagem" maxlength="2000" ${disabledPrev ? 'disabled' : ''}>${esc(this.previsaoMensagemCliente)}</textarea>
              </div>
              <button class="smallbtn gold" data-acao="salvar-previsao" ${disabledPrev ? 'disabled' : ''}>${this.enviandoPrevisao ? 'Salvando…' : 'Salvar previsão'}</button>
              ${this.mensagemPrevisao ? `<p class="erro">${esc(this.mensagemPrevisao)}</p>` : ''}`
                : ''
            }
          </div>
          <div>
            <h2>Equipe responsável</h2>
            <div class="row"><span>Colaborador</span><b>${tecnico ? esc(tecnico.nome) : 'Não atribuído'}</b></div>
            <div class="row"><span>Serviço</span><b>${categoria ? esc(categoria.nome) : '—'}</b></div>
            ${
              pode
                ? `
              <div class="field"><label>Colaborador responsável</label>
                <select id="eq-colaborador" ${disabledEq ? 'disabled' : ''}>
                  <option value="">Nenhum</option>
                  ${this.usuarios.map((u) => `<option value="${esc(u.id)}" ${u.id === ops.tecnicoId ? 'selected' : ''}>${esc(u.nome)}</option>`).join('')}
                </select>
              </div>
              <div class="field"><label>Serviço atribuído</label>
                <select id="eq-categoria" ${disabledEq || this.categorias.length === 0 ? 'disabled' : ''}>
                  <option value="">Nenhum</option>
                  ${this.categorias.map((c) => `<option value="${esc(c.id)}" ${c.id === ops.categoriaServicoId ? 'selected' : ''}>${esc(c.nome)}</option>`).join('')}
                </select>
              </div>
              ${this.categorias.length === 0 ? '<p class="nota">Nenhuma categoria de serviço cadastrada (Comissões → Categorias).</p>' : ''}
              <button class="smallbtn gold" data-acao="salvar-equipe" ${disabledEq ? 'disabled' : ''}>${this.enviandoEquipe ? 'Salvando…' : 'Atribuir / salvar'}</button>
              ${this.mensagemEquipe ? `<p class="erro">${esc(this.mensagemEquipe)}</p>` : ''}`
                : ''
            }
          </div>
        </div>
      </div>`;
  }

  private cardNotaInterna(): string {
    if (!this._permissoes.atualizar) return '';
    if (this.pedido!.status !== 'pago') {
      return `
      <div class="card full">
        <h2>Observações internas</h2>
        <p class="nota">As observações internas ficam disponíveis quando o pedido está pago e entra no fluxo operacional.</p>
      </div>`;
    }
    return `
      <div class="card full">
        <h2>Observações internas</h2>
        <p class="nota">Cada nota é registrada na timeline como evento interno, com o status operacional atual. Nunca é exibida ao cliente.</p>
        <div class="field">
          <textarea id="nota-interna" placeholder="Escreva uma observação interna…" maxlength="2000" ${this.enviandoNotaInterna ? 'disabled' : ''}></textarea>
        </div>
        <div class="direita">
          <button class="smallbtn" data-acao="salvar-nota-interna" ${this.enviandoNotaInterna ? 'disabled' : ''}>${this.enviandoNotaInterna ? 'Salvando…' : 'Salvar nota interna'}</button>
        </div>
        ${this.mensagemNotaInterna ? `<p class="erro">${esc(this.mensagemNotaInterna)}</p>` : ''}
      </div>`;
  }

  private cardAlterarStatus(): string {
    if (!this._permissoes.atualizar) return '';
    const p = this.pedido!;

    if (p.status !== 'pago') {
      return `
      <div class="card">
        <h2>Status operacional</h2>
        <div class="notice">Este pedido ainda não foi pago. O fluxo operacional (Solicitado → … → Entregue) só começa depois da confirmação do pagamento — "aguardando pagamento" é status <b>comercial</b>, não uma etapa de produção.</div>
      </div>`;
    }

    return `
      <div class="card">
        <h2>Alterar status operacional</h2>
        <div class="field"><label>Status operacional atual</label>
          <span class="status-badge">${this.eventos.length > 0 ? esc(ROTULO_STATUS_OPERACIONAL[this.eventoMaisRecenteStatus()].toUpperCase()) : 'SEM ETAPA REGISTRADA'}</span>
        </div>
        <div class="field"><label>Novo status</label>
          <select id="ev-status" ${this.enviandoEvento ? 'disabled' : ''}>
            ${STATUS_OPERACIONAL_PEDIDO.map((v) => `<option value="${v}" ${v === this.novoEventoStatus ? 'selected' : ''}>${esc(ROTULO_STATUS_OPERACIONAL[v])}</option>`).join('')}
          </select>
        </div>
        <div class="field"><label>Mensagem para o cliente (visível no Portal do Cliente)</label>
          <textarea id="ev-mensagem" maxlength="2000" placeholder="${esc(MENSAGEM_PUBLICA_PADRAO_STATUS_OPERACIONAL[this.novoEventoStatus])}" ${this.enviandoEvento ? 'disabled' : ''}>${esc(this.novoEventoMensagem)}</textarea>
          <p class="nota">Se deixar em branco, o cliente recebe automaticamente a mensagem padrão desta etapa (acima). Escreva algo só se quiser personalizar.</p>
        </div>
        <div class="field"><label>Nota interna (nunca visível ao cliente)</label>
          <textarea id="ev-nota" maxlength="2000" placeholder="Somente equipe interna…" ${this.enviandoEvento ? 'disabled' : ''}>${esc(this.novoEventoNota)}</textarea>
        </div>
        <button class="btn gold largura-total" data-acao="registrar-evento" ${this.enviandoEvento ? 'disabled' : ''}>${this.enviandoEvento ? 'Salvando…' : 'Salvar atualização'}</button>
        ${this.mensagemEvento ? `<p class="erro">${esc(this.mensagemEvento)}</p>` : ''}
      </div>`;
  }

  private cardTimeline(): string {
    const p = this.pedido!;
    const admin = this._permissoes.atualizar;
    const marcos: string[] = [];
    marcos.push(evento('Pedido criado', formatarDataHora(p.criadoEm), '<p>Pedido registrado no sistema.</p>', 'done'));
    if (p.pagoEm) marcos.push(evento('Pagamento confirmado', formatarDataHora(p.pagoEm), p.gatewayPagamento ? `<p>Forma: ${esc(ROTULO_GATEWAY[p.gatewayPagamento] ?? p.gatewayPagamento)}.</p>` : '', 'done'));
    if (p.canceladoEm) marcos.push(evento('Pedido cancelado', formatarDataHora(p.canceladoEm), '', 'cancel'));
    if (p.arquivadoEm) marcos.push(evento('Pedido arquivado', formatarDataHora(p.arquivadoEm), '', 'done'));

    const eventos = this.eventos
      .map((ev) => {
        const partes: string[] = [];
        if (ev.mensagemCliente) partes.push(`<p class="msg-cliente"><span>Mensagem ao cliente:</span> ${esc(ev.mensagemCliente)}</p>`);
        if (admin && ev.notaInterna) partes.push(`<p class="nota-interna"><span>Nota interna:</span> ${esc(ev.notaInterna)}</p>`);
        return evento(ROTULO_STATUS_OPERACIONAL[ev.statusOperacional], formatarDataHora(ev.criadoEm), partes.join(''), 'done');
      })
      .join('');

    return `
      <div class="card">
        <h2>Timeline do pedido</h2>
        <div class="timeline">
          ${marcos.join('')}
          ${eventos}
        </div>
        ${this.eventos.length === 0 ? '<p class="nota">Nenhuma atualização operacional registrada ainda.</p>' : ''}
      </div>`;
  }

  private cardImprimirExportar(): string {
    return `
      <div class="card">
        <h2>Imprimir / Exportar</h2>
        <button class="smallbtn largura-total" data-acao="doc:official:imprimir">Imprimir OPS / Orçamento (PDF)</button>
        <button class="smallbtn largura-total" data-acao="imprimir">Imprimir resumo da tela</button>
        <button class="smallbtn largura-total" data-acao="exportar">Exportar dados do pedido (CSV)</button>
        <p class="nota">A impressão da OPS abre o PDF real do documento — não a página do ERP.</p>
      </div>`;
  }

  private cardAcoes(): string {
    if (!this._permissoes.atualizar) return '';
    const p = this.pedido!;
    const podePagar = p.status === 'pendente';
    const podeCancelar = p.status !== 'cancelado';
    const podeArquivar = !p.arquivadoEm && p.status !== 'pago';
    const podeRestaurar = !!p.arquivadoEm;

    return `
      <div class="card">
        <h2>Ações do pedido</h2>
        ${
          podePagar
            ? `<div class="acao-linha">
                <select id="gateway" ${this.enviandoAcao ? 'disabled' : ''}>
                  ${GATEWAYS.map((g) => `<option value="${g.valor}">${g.rotulo}</option>`).join('')}
                </select>
                <button class="smallbtn gold" data-acao="pagar" ${this.enviandoAcao ? 'disabled' : ''}>Marcar como pago</button>
              </div>`
            : ''
        }
        ${
          podeCancelar
            ? this.mostrarFormCancelar
              ? `<div class="acao-coluna">
                  <input id="motivo-cancelar" type="text" placeholder="Motivo do cancelamento" maxlength="500" ${this.enviandoAcao ? 'disabled' : ''} />
                  <div class="acao-linha">
                    <button class="smallbtn red" data-acao="confirmar-cancelar" ${this.enviandoAcao ? 'disabled' : ''}>Confirmar cancelamento</button>
                    <button class="smallbtn" data-acao="desistir-cancelar">Voltar</button>
                  </div>
                </div>`
              : `<button class="smallbtn red largura-total" data-acao="abrir-cancelar">Cancelar pedido</button>`
            : ''
        }
        ${podeArquivar ? `<button class="smallbtn largura-total" data-acao="arquivar" ${this.enviandoAcao ? 'disabled' : ''}>Arquivar pedido</button>` : ''}
        ${podeRestaurar ? `<button class="smallbtn largura-total" data-acao="restaurar" ${this.enviandoAcao ? 'disabled' : ''}>Restaurar pedido</button>` : ''}
        ${this.mensagemAcao ? `<p class="erro">${esc(this.mensagemAcao)}</p>` : ''}
      </div>`;
  }

  // ------------------------------------------------------------------ wiring

  private wire(): void {
    const q = <T extends HTMLElement>(sel: string) => this.root.querySelector<T>(sel);

    this.root.querySelectorAll<HTMLElement>('[data-acao]').forEach((el) => {
      el.addEventListener('click', () => this.executarAcao(el.dataset['acao'] ?? ''));
    });

    const evStatus = q<HTMLSelectElement>('#ev-status');
    evStatus?.addEventListener('change', () => {
      this.novoEventoStatus = evStatus.value as StatusOperacionalPedido;
      // re-render só para atualizar o placeholder da mensagem padrão (o texto digitado já
      // está em `novoEventoMensagem`/`novoEventoNota` e volta na re-renderização).
      this.render();
    });
    q<HTMLTextAreaElement>('#ev-mensagem')?.addEventListener('input', (e) => {
      this.novoEventoMensagem = (e.target as HTMLTextAreaElement).value;
    });
    q<HTMLTextAreaElement>('#ev-nota')?.addEventListener('input', (e) => {
      this.novoEventoNota = (e.target as HTMLTextAreaElement).value;
    });
    q<HTMLInputElement>('#prev-data')?.addEventListener('input', (e) => {
      this.previsaoDataHora = (e.target as HTMLInputElement).value;
    });
    q<HTMLTextAreaElement>('#prev-mensagem')?.addEventListener('input', (e) => {
      this.previsaoMensagemCliente = (e.target as HTMLTextAreaElement).value;
    });
    q<HTMLInputElement>('#rastreio-transportadora')?.addEventListener('input', (e) => {
      this.rastreioTransportadora = (e.target as HTMLInputElement).value;
    });
    q<HTMLInputElement>('#rastreio-codigo')?.addEventListener('input', (e) => {
      this.rastreioCodigo = (e.target as HTMLInputElement).value;
    });
  }

  private executarAcao(acao: string): void {
    const q = <T extends HTMLElement>(sel: string) => this.root.querySelector<T>(sel);

    if (acao.startsWith('doc:')) {
      const [, tipo, modo] = acao.split(':') as ['doc', 'official' | 'production', 'ver' | 'baixar' | 'imprimir'];
      void this.abrirDocumento(tipo, modo);
      return;
    }

    switch (acao) {
      case 'voltar':
        this.voltar();
        break;
      case 'imprimir':
        window.print();
        break;
      case 'exportar':
        this.exportarCsv();
        break;
      case 'registrar-evento':
        this.novoEventoMensagem = q<HTMLTextAreaElement>('#ev-mensagem')?.value ?? this.novoEventoMensagem;
        this.novoEventoNota = q<HTMLTextAreaElement>('#ev-nota')?.value ?? this.novoEventoNota;
        this.novoEventoStatus = (q<HTMLSelectElement>('#ev-status')?.value as StatusOperacionalPedido) ?? this.novoEventoStatus;
        void this.registrarEvento();
        break;
      case 'pagamento-manual':
        void this.confirmarPagamentoManual(
          q<HTMLInputElement>('#pm-origem')?.value ?? '',
          q<HTMLTextAreaElement>('#pm-mensagem')?.value ?? ''
        );
        break;
      case 'salvar-previsao':
        this.previsaoDataHora = q<HTMLInputElement>('#prev-data')?.value ?? this.previsaoDataHora;
        this.previsaoMensagemCliente = q<HTMLTextAreaElement>('#prev-mensagem')?.value ?? this.previsaoMensagemCliente;
        void this.salvarPrevisao(this.previsaoDataHora, this.previsaoMensagemCliente);
        break;
      case 'salvar-equipe':
        void this.salvarEquipe(
          q<HTMLSelectElement>('#eq-colaborador')?.value ?? '',
          q<HTMLSelectElement>('#eq-categoria')?.value ?? ''
        );
        break;
      case 'salvar-nota-interna':
        void this.adicionarNotaInterna(q<HTMLTextAreaElement>('#nota-interna')?.value ?? '');
        break;
      case 'salvar-rastreio':
        this.rastreioTransportadora = q<HTMLInputElement>('#rastreio-transportadora')?.value ?? this.rastreioTransportadora;
        this.rastreioCodigo = q<HTMLInputElement>('#rastreio-codigo')?.value ?? this.rastreioCodigo;
        void this.salvarRastreio();
        break;
      case 'copiar-rastreio':
        void this.copiarCodigoRastreio();
        break;
      case 'pagar':
        void this.pagar((q<HTMLSelectElement>('#gateway')?.value ?? 'mercado_pago') as GatewayPagamento);
        break;
      case 'abrir-cancelar':
        this.mostrarFormCancelar = true;
        this.render();
        break;
      case 'desistir-cancelar':
        this.mostrarFormCancelar = false;
        this.mensagemAcao = '';
        this.render();
        break;
      case 'confirmar-cancelar':
        void this.cancelar(q<HTMLInputElement>('#motivo-cancelar')?.value ?? '');
        break;
      case 'arquivar':
        void this.arquivar();
        break;
      case 'restaurar':
        void this.restaurar();
        break;
    }
  }
}

// ------------------------------------------------------------------ helpers

const DASH = '<span class="nao-informado">—</span>';
const DASH_TXT = '—';

function esc(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

function brl(centavos: number): string {
  return esc(formatBRL(centavosParaReais(centavos)));
}

function brlTexto(centavos: number): string {
  return formatBRL(centavosParaReais(centavos));
}

function idCurto(id: string): string {
  return id.length > 8 ? id.slice(0, 8) : id;
}

/** ISO → valor de `<input type="datetime-local">` (local, sem timezone), truncado a minutos. */
function isoParaDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function evento(titulo: string, data: string, corpo: string, tipo: 'done' | 'cancel'): string {
  return `
    <div class="event ${tipo}">
      <span class="dot">${tipo === 'cancel' ? '✕' : '✓'}</span>
      <strong>${esc(titulo)}</strong>
      <small>${esc(data)}</small>
      ${corpo}
    </div>`;
}

const CSS = `
  :host { display: block; font-family: var(--pg-font-familia-base, Inter, system-ui, sans-serif); color: var(--pg-color-texto-primario, #f3f4f6); max-width: 100%; overflow-x: clip; }
  * { box-sizing: border-box; }
  .grid > *, .cards > *, .dupla > *, .produto > * { min-width: 0; }
  .top { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; border-bottom: 1px solid var(--pg-color-borda, #292d34); padding-bottom: 18px; margin-bottom: 18px; flex-wrap: wrap; }
  .crumb { color: var(--pg-color-texto-secundario, #9299a5); font-size: 11px; margin-bottom: 8px; }
  h1 { font-size: 22px; margin: 0 0 7px; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  .sub { color: var(--pg-color-texto-secundario, #9299a5); font-size: 13px; }
  .ref-tecnica { font-family: var(--pg-font-familia-mono, monospace); font-size: 11px; opacity: 0.7; }
  .tag-arquivado { color: var(--pg-color-alerta, #eabf32); }
  .actions { display: flex; gap: 8px; flex-wrap: wrap; }

  .btn { border: 1px solid var(--pg-color-borda, #292d34); background: var(--pg-color-bg-elevado, #1a1d22); color: var(--pg-color-texto-primario, #fff); border-radius: 7px; padding: 9px 14px; font: inherit; font-size: 13px; cursor: pointer; }
  .btn:hover { border-color: var(--pg-color-texto-secundario, #565b66); }
  .btn.gold { background: var(--pg-color-dourado, #eabf32); color: #111; border-color: var(--pg-color-dourado, #eabf32); font-weight: 700; }
  .btn.voltar { font-weight: 600; }
  .btn[disabled], .smallbtn[disabled] { opacity: .55; cursor: default; }
  .largura-total { width: 100%; }

  .grid { display: grid; grid-template-columns: minmax(0, 1fr) 340px; gap: 14px; align-items: start; }
  .cards { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 12px; }
  .card { background: var(--pg-color-bg-elevado, #16191e); border: 1px solid var(--pg-color-borda, #292d34); border-radius: 10px; padding: 15px; }
  .cards > .full { grid-column: 1 / -1; }
  .card h2 { font-size: 14px; margin: 0 0 14px; display: flex; justify-content: space-between; align-items: baseline; gap: 8px; }

  .row { display: flex; justify-content: space-between; gap: 6px 12px; padding: 8px 0; border-bottom: 1px solid var(--pg-color-borda, #24282e); font-size: 13px; flex-wrap: wrap; }
  .row:last-child { border-bottom: 0; }
  .row > span:first-child { color: var(--pg-color-texto-secundario, #9299a5); flex-shrink: 0; }
  .row b, .row .value { font-weight: 650; text-align: right; min-width: 0; overflow-wrap: anywhere; margin-left: auto; }
  .row.total { font-size: 17px; padding-top: 12px; }
  .green { color: var(--pg-color-sucesso, #39d98a); }
  .gold-text { color: var(--pg-color-dourado, #f5cf4d); }
  .muted, .nao-informado { color: var(--pg-color-texto-secundario, #9299a5); }
  .nota { color: var(--pg-color-texto-secundario, #747b87); font-size: 11px; margin: 10px 0 0; line-height: 1.5; }
  .erro { color: var(--pg-color-erro, #ef4444); font-size: 12px; margin: 8px 0 0; }
  .endereco { margin: 0 0 4px; font-size: 13px; }
  .direita { text-align: right; margin-top: 8px; }

  .link, a.link-secao, a.smallbtn.link { color: var(--pg-color-dourado, #eabf32); text-decoration: none; }
  a.link-secao { font-size: 11px; font-weight: 500; }
  .link:hover, a.link-secao:hover { text-decoration: underline; }

  .produto { display: grid; grid-template-columns: 52px minmax(0, 1fr) auto auto; gap: 10px 12px; align-items: center; border-top: 1px solid var(--pg-color-borda, #292d34); padding-top: 12px; margin-top: 4px; }
  .produto b, .produto div { overflow-wrap: anywhere; }
  .produto:first-of-type { border-top: 0; }
  .produto-img { width: 60px; height: 45px; border-radius: 8px; overflow: hidden; background: linear-gradient(145deg, #3a3f47, #23262c); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .produto-img img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .produto-img--vazia { background: var(--pg-color-bg-elevado, #171a1f); border: 1px dashed var(--pg-color-borda, #33383f); }
  .produto-img--vazia span { font-family: var(--pg-font-familia-display, system-ui); font-weight: 800; font-size: 13px; letter-spacing: 0.04em; color: var(--pg-color-texto-desabilitado, #5c626b); }

  /* Envio e rastreamento */
  .rastreio-salvo { display: grid; gap: 2px; }
  .rastreio-salvo .mono { font-family: var(--pg-font-familia-mono, monospace); letter-spacing: 0.03em; }
  .rastreio-acoes { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 10px; }
  .rastreio-acoes .smallbtn, .rastreio-acoes a.smallbtn { margin-top: 0; }
  .rastreio-form { display: grid; gap: 10px; margin-top: 14px; padding-top: 12px; border-top: 1px solid var(--pg-color-borda, #292d34); }
  .rastreio-form label { display: grid; gap: 4px; font-size: 12px; color: var(--pg-color-texto-secundario, #9299a5); }
  .rastreio-form .op { color: var(--pg-color-texto-desabilitado, #5c626b); font-weight: 400; }
  .rastreio-form input { padding: 8px 10px; border-radius: 7px; border: 1px solid var(--pg-color-borda, #292d34); background: var(--pg-color-bg-base, #12151a); color: var(--pg-color-texto-primario, #fff); font: inherit; font-size: 13px; }
  .rastreio-form .smallbtn { justify-self: start; margin-top: 2px; }
  .nota-acao { color: var(--pg-color-dourado, #eabf32); font-size: 11px; margin: 10px 0 0; }

  .chips { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
  .chip { background: var(--pg-color-bg-elevado, #171a1f); border: 1px solid var(--pg-color-borda, #292d34); border-radius: 9px; padding: 12px; font-size: 12px; }
  .chip b { display: block; margin: 6px 0 2px; font-size: 13px; }
  .chip small { color: var(--pg-color-texto-secundario, #9299a5); }

  .status-badge { border: 1px solid var(--pg-color-borda, #7343a2); color: #d69bff; background: rgba(124, 58, 173, .18); border-radius: 20px; padding: 4px 10px; font-size: 10px; font-weight: 700; letter-spacing: .04em; display: inline-block; }
  .status-badge.pago { border-color: var(--pg-color-sucesso, #39d98a); color: var(--pg-color-sucesso, #39d98a); background: rgba(57, 217, 138, .14); }
  .status-badge.cancelado { border-color: var(--pg-color-erro, #ef4444); color: var(--pg-color-erro, #ef4444); background: rgba(239, 68, 68, .14); }
  .status-badge.indisponivel { border-color: var(--pg-color-borda, #3a3f47); color: var(--pg-color-texto-secundario, #9299a5); background: transparent; }

  .doc-bloco { padding: 12px 0; border-bottom: 1px solid var(--pg-color-borda, #24282e); }
  .doc-bloco:last-of-type { border-bottom: 0; }
  .doc-linha { display: flex; justify-content: space-between; align-items: center; gap: 12px; }
  .document-actions { display: flex; gap: 7px; flex-wrap: wrap; margin-top: 8px; }
  .document-actions .smallbtn { margin-top: 0; }

  .dupla { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 20px; }
  .dupla h2 { margin-top: 0; }

  .field { margin: 10px 0; }
  .field label { display: block; color: var(--pg-color-texto-secundario, #a8aeb8); font-size: 11px; margin-bottom: 6px; }
  input, select, textarea { width: 100%; background: var(--pg-color-bg-base, #0d0f12); color: var(--pg-color-texto-primario, #eee); border: 1px solid var(--pg-color-borda, #343941); border-radius: 7px; padding: 9px 10px; outline: none; font: inherit; font-size: 13px; }
  textarea { min-height: 66px; resize: vertical; }
  input:focus, select:focus, textarea:focus { border-color: var(--pg-color-dourado, #eabf32); }
  .form-bloco { margin-top: 12px; padding-top: 12px; border-top: 1px solid var(--pg-color-borda, #24282e); }

  .smallbtn { padding: 8px 12px; border-radius: 7px; border: 1px solid var(--pg-color-borda, #292d34); background: var(--pg-color-bg-elevado, #20242a); color: var(--pg-color-texto-primario, #fff); font: inherit; font-size: 12px; cursor: pointer; display: inline-block; text-align: center; margin-top: 8px; }
  .smallbtn:hover { border-color: var(--pg-color-texto-secundario, #565b66); }
  .smallbtn.gold { background: var(--pg-color-dourado, #eabf32); color: #111; border-color: var(--pg-color-dourado, #eabf32); font-weight: 700; }
  .smallbtn.red { border-color: #633033; color: #ff7777; }

  .acao-linha { display: flex; gap: 8px; align-items: center; margin-top: 8px; flex-wrap: wrap; }
  .acao-linha select { width: auto; flex: 1; }
  .acao-linha .smallbtn { margin-top: 0; }
  .acao-coluna { margin-top: 8px; }

  .notice { background: rgba(239, 68, 68, .1); border: 1px solid #623035; color: #f2a1a1; border-radius: 7px; padding: 10px; margin: 10px 0; font-size: 11px; line-height: 1.5; }

  .timeline { position: relative; padding-left: 26px; margin-top: 4px; }
  .timeline:before { content: ""; position: absolute; left: 7px; top: 6px; bottom: 6px; width: 2px; background: var(--pg-color-borda, #3d3449); }
  .event { position: relative; padding: 0 0 18px; }
  .event:last-child { padding-bottom: 0; }
  .event .dot { position: absolute; left: -26px; top: 0; width: 17px; height: 17px; border-radius: 50%; background: rgba(124, 58, 173, .25); border: 2px solid var(--pg-color-dourado, #a855f7); display: grid; place-items: center; font-size: 8px; color: #fff; }
  .event.cancel .dot { border-color: var(--pg-color-erro, #ef4444); background: rgba(239, 68, 68, .2); }
  .event strong { display: block; font-size: 13px; }
  .event small { display: block; color: var(--pg-color-texto-secundario, #9299a5); font-size: 11px; margin-top: 1px; }
  .event p { margin: 5px 0 0; font-size: 12px; line-height: 1.45; }
  .event p span { color: var(--pg-color-texto-secundario, #9299a5); }
  .event p.msg-cliente span { color: var(--pg-color-sucesso, #39d98a); }
  .event p.nota-interna span { color: var(--pg-color-alerta, #eabf32); }

  .sticky { position: sticky; top: 12px; display: flex; flex-direction: column; gap: 14px; }

  @media (max-width: 1000px) {
    .grid { grid-template-columns: minmax(0, 1fr); }
    .sticky { position: static; }
  }
  @media (max-width: 720px) {
    .cards, .dupla { grid-template-columns: minmax(0, 1fr); }
    .chips { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
  }
  @media (max-width: 460px) {
    .chips { grid-template-columns: minmax(0, 1fr); }
    .produto { grid-template-columns: 52px minmax(0, 1fr); }
    .produto > b { grid-column: 2; text-align: left; }
    .top { gap: 10px; }
    h1 { font-size: 19px; }
  }
`;

customElements.define('erp-pedido-detalhe-painel', ErpPedidoDetalhePainel);

declare global {
  interface HTMLElementTagNameMap {
    'erp-pedido-detalhe-painel': ErpPedidoDetalhePainel;
  }
}
